import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import { FormCard, PrimaryButton, ChevronRightIcon } from './ui'
import ClaimIncidentDamageDescribeForm from './ClaimIncidentDamageDescribeForm'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import {
  buildClaimOtherSubtypeHref,
  buildClaimOtherSingleVehicleCollisionWithHref,
  buildClaimOtherSingleVehicleDriverHref,
  buildClaimOtherSingleVehicleReviewHref,
  formatClaimPolicySummaryLine,
  formatIncidentRailSummary,
  formatMultiVehicleCollisionRailSummary,
  formatMultiVehicleOtherPartyRailSummary,
  formatSingleVehicleCollisionRailSummary,
  OTHER_CLAIM_SUBTYPE_QUERY_KEY,
} from '../claims/claimFlowModel'
import { parseOtherClaimSubtypeQuery, type OtherClaimSubtypeId } from '../claims/otherClaimSubtypeOptions'
import {
  getSingleVehicleDescribeParts,
  isSingleVehicleCollisionTargetGateSatisfied,
  isSingleVehicleDriverDetailsGateSatisfied,
  persistSingleVehicleDescribeNarratives,
  readClaimDraft,
} from '../claims/claimDraftStorage'
import { useClaimFormSubmitAttempt } from '../claims/claimFormValidationFeedback'

const DESCRIBE_TEXTAREA_CLASS =
  'min-h-[168px] w-full resize-y rounded-xl border border-solid border-border-primary bg-background-primary px-[16px] py-[14px] font-body text-sm leading-sm text-text-primary-900 shadow-xs placeholder:text-text-placeholder focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2'

const MAX_FIELD_LENGTH = 1000

const SINGLE_VEHICLE_SUBTYPE = 'single-vehicle' as const satisfies OtherClaimSubtypeId

function initialDescribeState(): { incident: string; damage: string } {
  return getSingleVehicleDescribeParts(readClaimDraft())
}

/**
 * Single-vehicle collision: free-text incident narrative and damage details before confirm/submit.
 */
export default function ClaimOtherSingleVehicleDescribeStep() {
  const navigate = useNavigate()
  const location = useLocation()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const claimSubtype =
    parseOtherClaimSubtypeQuery(queryParams.get(OTHER_CLAIM_SUBTYPE_QUERY_KEY)) ??
    parseOtherClaimSubtypeQuery(readClaimDraft()?.otherClaimSubtypeId ?? null)
  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null

  const [{ incident, damage }, setPair] = useState(initialDescribeState)

  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (claimSubtype === null || claimSubtype !== SINGLE_VEHICLE_SUBTYPE) {
      navigate(buildClaimOtherSubtypeHref(policyRef, typeInQuery), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    const d = readClaimDraft()
    if (!isSingleVehicleDriverDetailsGateSatisfied(d)) {
      navigate(buildClaimOtherSingleVehicleDriverHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    if (!isSingleVehicleCollisionTargetGateSatisfied(d)) {
      navigate(buildClaimOtherSingleVehicleCollisionWithHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
    }
  }, [claimSubtype, navigate, navState?.policy, policyRef, typeInQuery])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      persistSingleVehicleDescribeNarratives(policyRef, incident, damage)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [damage, incident, policyRef])

  const persistNow = () => {
    persistSingleVehicleDescribeNarratives(policyRef, incident, damage)
  }

  const backToCollision = () => {
    persistNow()
    if (claimSubtype === null) return
    const path = buildClaimOtherSingleVehicleCollisionWithHref(policyRef, typeInQuery, claimSubtype)
    if (navState?.policy) {
      navigate(path, { state: { policy: navState.policy } })
    } else {
      navigate(path)
    }
  }

  const policySummary = formatClaimPolicySummaryLine(
    navState?.policy ?? (policyRef ? { policyRef } : null)
  )

  const canProceed = incident.trim().length > 0 && damage.trim().length > 0
  const { submitAttempted, showSubmitSummary, attemptSubmit, clearSubmitAttempt } =
    useClaimFormSubmitAttempt(canProceed)
  const fieldErrors = useMemo(() => {
    if (!submitAttempted) return { incident: false, damage: false }
    return {
      incident: incident.trim().length === 0,
      damage: damage.trim().length === 0,
    }
  }, [submitAttempted, incident, damage])

  const handleNext = () => {
    attemptSubmit(() => {
      if (claimSubtype === null) return
      persistNow()
      const path = buildClaimOtherSingleVehicleReviewHref(policyRef, typeInQuery, claimSubtype)
      navigate(path, {
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
    })
  }

  const draft = readClaimDraft()

  if (claimSubtype === null || claimSubtype !== SINGLE_VEHICLE_SUBTYPE) {
    return (
      <div
        className="relative min-h-screen w-full bg-background-secondary"
        aria-busy="true"
        aria-label="Redirecting"
      />
    )
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back"
        onBack={backToCollision}
        title="Incident and damage"
      />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={policySummary}
            claimEntryBranch="other"
            otherClaimSubtypeId={claimSubtype}
            incidentSummary={formatIncidentRailSummary(draft)}
            multiVehicleCollisionSummary={formatMultiVehicleCollisionRailSummary(draft)}
            multiVehicleOtherPartySummary={formatMultiVehicleOtherPartyRailSummary(draft)}
            multiVehicleIncidentDescription={draft?.multiVehicleIncidentDescription ?? null}
            multiVehicleDamageDescription={draft?.multiVehicleDamageDescription ?? null}
            multiVehicleClaimDescription={draft?.multiVehicleClaimDescription ?? null}
            singleVehicleCollisionSummary={formatSingleVehicleCollisionRailSummary(draft ?? undefined)}
            singleVehicleIncidentDescription={incident}
            singleVehicleDamageDescription={damage}
            windscreenGstRegistered={draft?.windscreenGstRegistered ?? null}
            windscreenAbn={draft?.windscreenAbn ?? null}
            windscreenItcEntitled={draft?.windscreenItcEntitled ?? null}
            windscreenItcPercentage={draft?.windscreenItcPercentage ?? null}
          />
        }
      >
        <FormCard>
          <ClaimIncidentDamageDescribeForm
            incident={incident}
            damage={damage}
            onIncidentChange={(value) => setPair((p) => ({ ...p, incident: value }))}
            onDamageChange={(value) => setPair((p) => ({ ...p, damage: value }))}
            fieldErrors={fieldErrors}
            showSubmitSummary={showSubmitSummary}
            clearSubmitAttempt={clearSubmitAttempt}
            maxLength={MAX_FIELD_LENGTH}
            textareaClassName={DESCRIBE_TEXTAREA_CLASS}
          />
        </FormCard>

        <div className="sticky bottom-0 w-full bg-background-secondary pt-[8px] pb-[8px] md:static md:py-0">
          <PrimaryButton
            type="button"
            onClick={handleNext}
            size="lg"
            fullWidth
            rightIcon={
              <div className="size-5 text-text-white">
                <ChevronRightIcon />
              </div>
            }
            aria-label="Next"
          >
            Next
          </PrimaryButton>
        </div>
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
